import React from 'react';
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, UseFormReturn, FieldErrors } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { Delete } from '@mui/icons-material';
import { InvoiceFileUploader } from './InvoiceFileUploader';
import { UploadInvoiceFormFields } from './types';
import { useCampaign } from 'hooks';

type UploadInvoiceFormProps = {
  form: Pick<UseFormReturn<UploadInvoiceFormFields>, 'register' | 'control' | 'watch' | 'setValue'> & {
    errors: FieldErrors<UploadInvoiceFormFields>;
  };
};

export const UploadInvoiceForm = ({ form: { register, errors, control, watch, setValue } }: UploadInvoiceFormProps) => {
  const { getAllCampaignsQuery } = useCampaign();
  const allCampaigns = getAllCampaignsQuery.data?.campaigns ?? [];

  const invoiceFile = watch('invoiceFile');
  const autoGenerate = watch('autoGenerate');

  const removeUploadedFile = () => {
    setValue('invoiceFile', null);
  };

  return (
    <Stack gap={3}>
      <TextField
        {...register('businessId', {
          required: 'Business ID is required',
          maxLength: { value: 50, message: 'Business ID must be less then 50 characters long' },
        })}
        variant="outlined"
        label="Business ID"
        error={!!errors.businessId}
        helperText={errors.businessId?.message}
      />
      <TextField
        {...register('amount', { required: 'Amount is required' })}
        variant="outlined"
        label="Amount"
        type="number"
        error={!!errors.amount}
        helperText={errors.amount?.message}
      />
      <Controller
        name="campaignId"
        control={control}
        rules={{ required: 'Campaign is required' }}
        render={({ field }) => (
          <Autocomplete
            fullWidth
            value={allCampaigns.find((campaign) => campaign.id === Number(field.value)) ?? null}
            options={allCampaigns}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <TextField
                placeholder="Select campaign"
                variant="outlined"
                {...params}
                error={!!errors.campaignId}
                helperText={errors.campaignId?.message}
              />
            )}
            getOptionLabel={(option) => `${option.name}`}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  <Stack spacing={1}>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="body3">ID: {option.id}</Typography>
                  </Stack>
                </li>
              );
            }}
            onChange={(_, newValue, reason) => {
              if (reason === 'clear') {
                field.onChange(null);
                return;
              }
              field.onChange(newValue?.id ?? null);
            }}
          />
        )}
      />
      <Controller
        name="date"
        control={control}
        rules={{ required: 'Date is required' }}
        render={({ field }) => (
          <DatePicker
            {...field}
            displayWeekNumber
            format="yyyy-MM-dd"
            label="Date"
            onChange={(newValue) => field.onChange(newValue)}
            slotProps={{
              textField: {
                error: !!errors.date,
                helperText: errors.date?.message,
              },
            }}
          />
        )}
      />

      <Controller
        name="autoGenerate"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={field.value}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                  // Clear file when switching to auto-generate
                  if (e.target.checked) {
                    setValue('invoiceFile', null);
                  }
                }}
              />
            }
            label="Auto-generate invoice"
          />
        )}
      />

      {!autoGenerate && (
        <>
          {invoiceFile ? (
            <Paper variant="outlined" sx={{ pl: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                <Typography variant="body1">{invoiceFile.name}</Typography>
                <IconButton sx={{ p: 2 }} size="small" onClick={removeUploadedFile}>
                  <Delete />
                </IconButton>
              </Stack>
            </Paper>
          ) : (
            <Controller
              name="invoiceFile"
              control={control}
              rules={{
                required: !autoGenerate ? 'Invoice file is required' : false,
              }}
              render={({ field }) => <InvoiceFileUploader onFileUpload={(file: File) => field.onChange(file)} />}
            />
          )}
          {errors.invoiceFile && (
            <Typography variant="caption" color="error">
              {errors.invoiceFile.message}
            </Typography>
          )}
        </>
      )}
    </Stack>
  );
};
